import React, { useEffect, useMemo, useState } from "react";
import PhotoGallery from "./PhotoGallery";

/**
 * ArchiveBlock
 *
 * Props:
 * - archiveTitle: string
 * - items: array of docs (each should have id, status, timestamp, dateOfCompletion, engineerNotes, etc.)
 * - onUnarchive: (id: string) => Promise<void> | void
 * - showArchiveKey: string  // localStorage key to persist show/hide
 * - enableDateFilter?: boolean // default false
 */
export default function ArchiveBlock({
  archiveTitle = "✅ Completed (Archive)",
  items = [],
  onUnarchive,
  showArchiveKey = "archive-visible",
  enableDateFilter = false,
}) {
  const [visible, setVisible] = useState(() => {
    const v = localStorage.getItem(showArchiveKey);
    return v ? v === "1" : false;
  });
  const [expandedId, setExpandedId] = useState(null);
  const [dateFilter, setDateFilter] = useState("");

  useEffect(() => {
    localStorage.setItem(showArchiveKey, visible ? "1" : "0");
  }, [visible, showArchiveKey]);

  const filtered = useMemo(() => {
    if (!enableDateFilter || !dateFilter) return items;
    return items.filter((j) => {
      const d = j?.dateOfCompletion?.toDate?.();
      if (!d) return false;
      return d.toISOString().slice(0, 10) === dateFilter;
    });
  }, [items, enableDateFilter, dateFilter]);

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">{archiveTitle}</h3>
        <button
          onClick={() => setVisible(!visible)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          {visible ? "Hide" : "View"}
        </button>
      </div>

      {enableDateFilter && visible && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Completion Date:
          </label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2 w-full"
          />
        </div>
      )}

      {visible && (
        <div className="mt-4">
          {filtered?.length ? (
            <ul className="space-y-4">
              {filtered.map((job) => {
                // headline heuristic: machines vs work_requests
                const headline = job.machine && job.fault
                  ? `${job.machine} - ${job.fault}`
                  : `${job.requestType || "Request"}${job.location ? " - " + job.location : ""}`;

                const submitted =
                  job.timestamp?.toDate?.()?.toLocaleString?.() || "N/A";
                const completed =
                  job.dateOfCompletion?.toDate?.()?.toLocaleString?.() || "N/A";

                return (
                  <li key={job.id} className="p-4 rounded shadow-md" style={{ backgroundColor: "#f3f4f6" }}>
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => toggleExpand(job.id)}
                    >
                      <div className="text-lg font-medium text-gray-700">
                        {headline}
                      </div>
                      <span className="text-gray-500">
                        {expandedId === job.id ? "▲" : "▼"}
                      </span>
                    </div>

                    {expandedId === job.id && (
                      <div className="mt-3 p-3 bg-white rounded border border-gray-300 space-y-3">
                        {/* Common fields */}
                        <p><strong>Reported By:</strong> {job.name || "N/A"}</p>
                        <p><strong>Submitted:</strong> {submitted}</p>
                        <p><strong>Completed:</strong> {completed}</p>

                        {/* Machines-specific */}
                        {job.faultType && (
                          <p><strong>Fault Type:</strong> {job.faultType}</p>
                        )}
                        {job.notes && (
                          <p><strong>Reported Notes:</strong> {job.notes}</p>
                        )}

                        {/* Work request specifics */}
                        {job.priority && (
                          <p><strong>Priority:</strong> {job.priority}</p>
                        )}
                        {job.description && (
                          <p><strong>Description:</strong> {job.description}</p>
                        )}

                        {job.engineerNotes && (
                          <p><strong>Engineer Notes:</strong> {job.engineerNotes}</p>
                        )}

                        {/* SharePoint bits (if WR doc has them) */}
                        {job.sharepoint?.shareUrl && (
                          <p className="mt-2">
                            <strong>Upload photos (internal link): </strong>
                            <a
                              className="text-indigo-700 underline break-all"
                              href={job.sharepoint.shareUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open upload folder
                            </a>
                          </p>
                        )}

                        {job.sharepoint?.folderItemId && (
                          <>
                            <p className="mt-2 font-semibold">Photos</p>
                            <PhotoGallery folderItemId={job.sharepoint.folderItemId} />
                          </>
                        )}

                        {typeof onUnarchive === "function" && (
                          <button
                            onClick={() => onUnarchive(job.id)}
                            className="w-full mt-2 bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700 transition text-lg font-semibold"
                          >
                            Unarchive
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-gray-500 mt-2">No archived items.</p>
          )}
        </div>
      )}
    </div>
  );
}
